/**
 * CallFoundry Audio Sync Rejection Guard (Directive 8)
 * 
 * Verifies that no event payload containing raw audio (Blob, File, ArrayBuffer,
 * Uint8Array, base64 audio URI, or large audio binary chunks) can ever be enqueued
 * or transmitted to the remote Cloud Firestore sync replica.
 * 
 * Invariant: Audio Blobs and recordings remain strictly local on-device in IndexedDB
 * (callfoundry_audio_store).
 */

export class AudioSyncRejectionError extends Error {
  constructor(message: string) {
    super(`AUDIO_PAYLOAD_REJECTED: ${message}`);
    this.name = 'AudioSyncRejectionError';
  }
}

export function containsRawAudioPayload(val: unknown, depth = 0): boolean {
  if (depth > 10 || val === null || val === undefined) return false;

  // 1. Direct binary types
  if (typeof Blob !== 'undefined' && val instanceof Blob) return true;
  if (typeof File !== 'undefined' && val instanceof File) return true;
  if (typeof ArrayBuffer !== 'undefined' && val instanceof ArrayBuffer) return true;
  if (typeof Uint8Array !== 'undefined' && val instanceof Uint8Array) return true;
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(val)) return true;

  // 2. Base64 audio data URI or audio payload patterns
  if (typeof val === 'string') {
    if (val.startsWith('data:audio/') || /^data:audio\/(wav|webm|mp3|ogg|aac|flac);base64,/i.test(val)) {
      return true;
    }
  }

  // 3. Arrays
  if (Array.isArray(val)) {
    for (const item of val) {
      if (containsRawAudioPayload(item, depth + 1)) return true;
    }
    return false;
  }

  // 4. Recursive inspection for objects
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      const lowerKey = key.toLowerCase();
      if (
        (lowerKey === 'audioblob' ||
          lowerKey === 'audiobuffer' ||
          lowerKey === 'rawaudio' ||
          lowerKey === 'audiobytes' ||
          lowerKey === 'recordingblob') &&
        obj[key]
      ) {
        return true;
      }
      if (containsRawAudioPayload(obj[key], depth + 1)) {
        return true;
      }
    }
  }

  return false;
}

export function assertNoRawAudioPayload(val: unknown): void {
  if (containsRawAudioPayload(val)) {
    throw new AudioSyncRejectionError(
      'Event payload contains raw audio (Blob, ArrayBuffer, or base64 audio). Audio recordings must remain strictly local in callfoundry_audio_store and are prohibited from sync transport.'
    );
  }
}
